import { useState, useEffect } from 'react';
import { X, Plus, Trash2, Target, Shield, ExternalLink } from 'lucide-react';
import type { Category, Task } from '../../../App';
import type { Project, ProjectTask } from '../../../types/project';
import type { PINCode } from '../VendorsScreen';
import type { MicrosoftAccount, SupervisorAccess } from '../PINCodesScreen';

interface ProjectFormModalProps {
  project?: Project | null;
  categories: Category[];
  allTasks: Task[];
  pinCodes: PINCode[];
  supervisorAccess: SupervisorAccess | null;
  microsoftAccounts: MicrosoftAccount[];
  onSave: (project: Project) => void;
  onClose: () => void;
  isOpen: boolean;
  onNavigateToManageAccess: () => void;
}

export function ProjectFormModal({
  project,
  categories,
  allTasks,
  pinCodes,
  supervisorAccess,
  microsoftAccounts,
  onSave,
  onClose,
  isOpen,
  onNavigateToManageAccess,
}: ProjectFormModalProps) {
  const [name, setName] = useState(project?.name || '');
  const [number, setNumber] = useState(project?.number || '');
  const [status, setStatus] = useState<Project['status']>(project?.status || 'active');
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>(project?.categoryIds || []);
  const [projectTasks, setProjectTasks] = useState<ProjectTask[]>(project?.tasks || []);

  // Reset form when project changes (for editing)
  useEffect(() => {
    if (isOpen && project) {
      setName(project.name || '');
      setNumber(project.number || '');
      setStatus(project.status || 'active');
      setSelectedCategoryIds(project.categoryIds || []);
      
      // When editing, sync project tasks with current task definitions
      // This ensures new questions added to tasks are available
      const syncedTasks = project.tasks.map(projectTask => {
        const originalTask = allTasks.find(t => t.id === projectTask.id);
        if (!originalTask) return projectTask; // Task no longer exists
        
        // Merge questions: keep existing ones with their goals, add new ones
        const syncedQuestions = originalTask.questions.map(originalQ => {
          const existingQ = projectTask.questions.find(pq => pq.id === originalQ.id);
          if (existingQ) {
            // Keep existing question with its goal
            return existingQ;
          } else {
            // New question added to task definition
            return {
              ...originalQ,
              goal: originalQ.type === 'number' ? 0 : undefined,
              currentProgress: 0
            };
          }
        });
        
        return {
          ...projectTask,
          questions: syncedQuestions,
          // Keep selectedQuestionIds as is - don't auto-select new questions
        };
      });
      
      setProjectTasks(syncedTasks);
    } else if (isOpen && !project) {
      // New project - reset to empty
      setName('');
      setNumber('');
      setStatus('active');
      setSelectedCategoryIds([]);
      setProjectTasks([]);
    }
  }, [isOpen, project, allTasks]);

  if (!isOpen) return null;

  const availableTasksForCategory = (categoryId: string) => {
    return allTasks.filter(t => t.categoryId === categoryId);
  };

  const isTaskSelected = (taskId: string) => {
    return projectTasks.some(pt => pt.id === taskId);
  };

  const isQuestionSelected = (taskId: string, questionId: string) => {
    const task = projectTasks.find(pt => pt.id === taskId);
    return task?.selectedQuestionIds?.includes(questionId) || false;
  };

  const areAllQuestionsSelected = (task: Task) => {
    const projectTask = projectTasks.find(pt => pt.id === task.id);
    if (!projectTask) return false;
    return task.questions.every(q => projectTask.selectedQuestionIds?.includes(q.id));
  };

  const toggleCategory = (categoryId: string) => {
    if (selectedCategoryIds.includes(categoryId)) {
      // Remove category and its tasks
      setSelectedCategoryIds(selectedCategoryIds.filter(id => id !== categoryId));
      setProjectTasks(projectTasks.filter(pt => pt.categoryId !== categoryId));
    } else {
      // Add category
      setSelectedCategoryIds([...selectedCategoryIds, categoryId]);
    }
  };

  const toggleTask = (task: Task) => {
    if (isTaskSelected(task.id)) {
      // Remove entire task
      setProjectTasks(projectTasks.filter(pt => pt.id !== task.id));
    } else {
      // Add task with all questions selected
      setProjectTasks([
        ...projectTasks,
        {
          ...task,
          questions: task.questions.map(q => ({ 
            ...q, 
            goal: q.type === 'number' ? 0 : undefined,
            currentProgress: 0 
          })),
          selectedQuestionIds: task.questions.map(q => q.id),
        },
      ]);
    }
  };

  const toggleQuestion = (task: Task, questionId: string) => {
    const existingTask = projectTasks.find(pt => pt.id === task.id);
    
    if (existingTask) {
      // Task exists - toggle question
      const isSelected = existingTask.selectedQuestionIds?.includes(questionId);
      
      if (isSelected) {
        // Deselect question
        const newSelectedQuestions = existingTask.selectedQuestionIds?.filter(id => id !== questionId) || [];
        
        if (newSelectedQuestions.length === 0) {
          // If no questions left, remove the task
          setProjectTasks(projectTasks.filter(pt => pt.id !== task.id));
        } else {
          // Update task with new selected questions
          setProjectTasks(
            projectTasks.map(pt =>
              pt.id === task.id
                ? { ...pt, selectedQuestionIds: newSelectedQuestions }
                : pt
            )
          );
        }
      } else {
        // Select question - ensure the question object exists in the questions array
        const questionFromOriginal = task.questions.find(q => q.id === questionId);
        const questionExists = existingTask.questions.some(q => q.id === questionId);
        
        setProjectTasks(
          projectTasks.map(pt =>
            pt.id === task.id
              ? { 
                  ...pt,
                  questions: questionExists 
                    ? pt.questions 
                    : [...pt.questions, { 
                        ...questionFromOriginal!, 
                        goal: questionFromOriginal!.type === 'number' ? 0 : undefined,
                        currentProgress: 0 
                      }],
                  selectedQuestionIds: [...(pt.selectedQuestionIds || []), questionId] 
                }
              : pt
          )
        );
      }
    } else {
      // Task doesn't exist - add it with just this question selected
      const question = task.questions.find(q => q.id === questionId);
      setProjectTasks([
        ...projectTasks,
        {
          ...task,
          questions: task.questions.map(q => ({ 
            ...q, 
            goal: q.id === questionId && q.type === 'number' ? 0 : undefined,
            currentProgress: 0 
          })),
          selectedQuestionIds: [questionId],
        },
      ]);
    }
  };

  const updateQuestionGoal = (taskId: string, questionId: string, goal: number) => {
    setProjectTasks(
      projectTasks.map(pt =>
        pt.id === taskId
          ? {
              ...pt,
              questions: pt.questions.map(q =>
                q.id === questionId ? { ...q, goal } : q
              ),
            }
          : pt
      )
    );
  };

  const handleSave = () => {
    if (!name.trim() || !number.trim()) {
      alert('Please enter project name and number');
      return;
    }

    if (projectTasks.length === 0) {
      alert('Please select at least one task');
      return;
    }

    const newProject: Project = {
      id: project?.id || `proj-${Date.now()}`,
      name: name.trim(),
      number: number.trim(),
      dateCreated: project?.dateCreated || new Date().toISOString().split('T')[0],
      status,
      categoryIds: selectedCategoryIds,
      taskIds: projectTasks.map(pt => pt.id),
      tasks: projectTasks,
    };

    onSave(newProject);
  };

  const hasNumericQuestion = (task: Task) => {
    return task.questions.some(q => q.type === 'number');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {project ? 'Edit Project' : 'Create New Project'}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Select categories and tasks for this project
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Project Details */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Project Name *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
                placeholder="e.g., Gama LLC – CTB & SAT"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Project Number *
              </label>
              <input
                type="text"
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
                placeholder="e.g., P-2024-001"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as Project['status'])}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
            >
              <option value="active">Active</option>
              <option value="on-hold">On Hold</option>
              <option value="completed">Completed</option>
            </select>
          </div>

          {/* Categories & Tasks Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Select Categories & Tasks *
            </label>
            <div className="border border-gray-200 rounded-lg divide-y divide-gray-200">
              {categories.map((category) => {
                const categoryTasks = availableTasksForCategory(category.id);
                const isCategorySelected = selectedCategoryIds.includes(category.id);
                
                return (
                  <div key={category.id}>
                    {/* Category Header */}
                    <div className="bg-gray-50 p-4">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isCategorySelected}
                          onChange={() => toggleCategory(category.id)}
                          className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                        />
                        <span className="font-semibold text-gray-900">{category.name}</span>
                        <span className="text-xs text-gray-500">({categoryTasks.length} tasks)</span>
                      </label>
                    </div>

                    {/* Tasks */}
                    {isCategorySelected && categoryTasks.length > 0 && (
                      <div className="p-4 space-y-4 bg-white">
                        {categoryTasks.map((task) => {
                          const selected = isTaskSelected(task.id);
                          const projectTask = projectTasks.find(pt => pt.id === task.id);
                          const allQuestionsSelected = areAllQuestionsSelected(task);

                          return (
                            <div key={task.id} className="space-y-2 border-l-2 border-gray-200 pl-4">
                              {/* Task Checkbox */}
                              <label className="flex items-center gap-3 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={allQuestionsSelected}
                                  onChange={() => toggleTask(task)}
                                  className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                />
                                <span className="font-medium text-gray-900">{task.title}</span>
                                <span className="text-xs text-gray-500">({task.questions.length} questions)</span>
                              </label>

                              {/* Questions List */}
                              {selected && (
                                <div className="ml-7 space-y-2">
                                  {task.questions.map((question) => {
                                    const questionSelected = isQuestionSelected(task.id, question.id);
                                    const questionInProject = projectTask?.questions.find(q => q.id === question.id);
                                    const showGoal = questionSelected && question.type === 'number';
                                    
                                    // Truncate long question text
                                    const truncatedQuestion = question.question.length > 60
                                      ? question.question.substring(0, 60) + '...'
                                      : question.question;

                                    return (
                                      <div key={question.id} className="space-y-1">
                                        <label className="flex items-center gap-2 cursor-pointer group">
                                          <input
                                            type="checkbox"
                                            checked={questionSelected}
                                            onChange={() => toggleQuestion(task, question.id)}
                                            className="w-3.5 h-3.5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                          />
                                          <span 
                                            className="text-sm text-gray-700 flex-1" 
                                            title={question.question}
                                          >
                                            {truncatedQuestion}
                                          </span>
                                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                                            {question.type}
                                          </span>
                                        </label>

                                        {/* Goal Input for Numeric Questions */}
                                        {showGoal && (
                                          <div className="ml-5 flex items-center gap-2 bg-indigo-50 px-3 py-2 rounded">
                                            <Target className="w-3.5 h-3.5 text-indigo-600" />
                                            <label className="text-xs text-gray-700 font-medium">Goal:</label>
                                            <input
                                              type="number"
                                              value={questionInProject?.goal || ''}
                                              onChange={(e) =>
                                                updateQuestionGoal(task.id, question.id, parseFloat(e.target.value) || 0)
                                              }
                                              className="w-24 px-2 py-1 border border-indigo-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none text-sm"
                                              placeholder="0"
                                              min="0"
                                            />
                                            <span className="text-xs text-gray-600">
                                              {question.question.match(/\(([^)]+)\)/)?.[1] || 'units'}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Selected Tasks Summary */}
          {projectTasks.length > 0 && (
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <h4 className="font-semibold text-indigo-900 mb-2">
                Selected Tasks ({projectTasks.length})
              </h4>
              <div className="space-y-1">
                {projectTasks.map((pt) => (
                  <div key={pt.id} className="text-sm text-indigo-700 flex items-center justify-between">
                    <span>{pt.title}</span>
                    {pt.goal !== undefined && (
                      <span className="text-xs bg-indigo-200 px-2 py-0.5 rounded">
                        Goal: {pt.goal}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Access Management - View Only for existing projects */}
          {project && project.id && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-blue-600" />
                  Who Has Access to This Project
                </h4>
                <button
                  onClick={() => {
                    onClose();
                    onNavigateToManageAccess();
                  }}
                  className="flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-700 transition"
                >
                  <ExternalLink className="w-4 h-4" />
                  Edit Access
                </button>
              </div>
              <div className="space-y-2">
                {(() => {
                  const hasAccess: { role: string; badge: string }[] = [];
                  
                  // Check PIN codes
                  pinCodes.forEach(pin => {
                    if (pin.projects?.includes(project.id)) {
                      hasAccess.push({ 
                        role: pin.vendorName, 
                        badge: 'vendor' 
                      });
                    }
                  });
                  
                  // Check approved Microsoft supervisor accounts
                  microsoftAccounts.forEach(account => {
                    if (account.isActive && account.projects?.includes(project.id)) {
                      hasAccess.push({
                        role: account.name ? `${account.name} (${account.email})` : account.email,
                        badge: 'supervisor'
                      });
                    }
                  });
                  
                  return hasAccess.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {hasAccess.map((access, index) => (
                        <span 
                          key={index}
                          className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm ${
                            access.badge === 'supervisor' 
                              ? 'bg-indigo-100 text-indigo-700 font-semibold' 
                              : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {access.role}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-600 italic">
                      No users have been granted access to this project yet.
                    </p>
                  );
                })()}
              </div>
              <p className="text-xs text-gray-500 mt-3">
                Access can only be modified from the Manage Access tab.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition font-medium"
          >
            {project ? 'Update Project' : 'Create Project'}
          </button>
        </div>
      </div>
    </div>
  );
}
